import React, { useMemo } from "react";
import { useTheme } from "../contexts/ThemeContext";
import { Country } from "country-state-city";
import Select from "react-select";

interface CountryPhoneInputProps {
    countryCode: string;
    phoneNumber: string;
    onCountryCodeChange: (code: string) => void;
    onPhoneNumberChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    error?: string;
    placeholder?: string;
}

export const getAllCountryCodes = () => {
    return Country.getAllCountries()
        .filter((c) => c.phonecode)
        .map((c) => ({
            value: c.phonecode.startsWith("+") ? c.phonecode : `+${c.phonecode}`,
            label: `${c.name} (${c.phonecode.startsWith("+") ? c.phonecode : `+${c.phonecode}`})`,
            code: c.phonecode.startsWith("+") ? c.phonecode : `+${c.phonecode}`, // legacy support
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
};

const CountryPhoneInput: React.FC<CountryPhoneInputProps> = ({
    countryCode,
    phoneNumber,
    onCountryCodeChange,
    onPhoneNumberChange,
    error,
    placeholder = "Enter phone number",
}) => {
    const { theme } = useTheme();

    const options = useMemo(() => getAllCountryCodes(), []);
    const selectedOption = options.find((o) => o.value === countryCode);

    const containerClass =
        theme === "dark"
            ? `bg-[#111111] border ${error ? "border-red-500" : "border-[#2C2C2C]"} focus-within:border-[#B6CB24]`
            : `bg-white border ${error ? "border-red-500" : "border-gray-300"} focus-within:border-[#B6CB24]`;

    const customStyles = {
        control: (base: any) => ({
            ...base,
            backgroundColor: "transparent",
            border: "none",
            boxShadow: "none",
            minHeight: "40px",
            width: "120px",
            cursor: "pointer",
        }),
        menu: (base: any) => ({
            ...base,
            backgroundColor: theme === "dark" ? "#1E1E1E" : "white",
            border: theme === "dark" ? "1px solid #2C2C2C" : "1px solid #E5E7EB",
            zIndex: 50,
        }),
        option: (base: any, state: any) => ({
            ...base,
            backgroundColor: state.isFocused
                ? theme === "dark" ? "#2C2C2C" : "#F3F4F6"
                : "transparent",
            color: theme === "dark" ? "#F3F3F6" : "#111827",
            fontSize: "0.875rem",
            cursor: "pointer",
        }),
        singleValue: (base: any) => ({
            ...base,
            color: theme === "dark" ? "#D1D5DB" : "#374151",
            fontSize: "0.875rem",
        }),
        valueContainer: (base: any) => ({
            ...base,
            padding: "0 8px",
        }),
        dropdownIndicator: (base: any) => ({
            ...base,
            padding: "0 4px",
        }),
        indicatorSeparator: (base: any) => ({
            ...base,
            display: "none",
        }),
    };

    return (
        <div className={`flex items-center rounded-md transition-colors overflow-visible ${containerClass}`}>
            <Select
                value={selectedOption}
                onChange={(option: any) => onCountryCodeChange(option.value)}
                options={options}
                styles={customStyles}
                placeholder="+00"
                isSearchable
            />
            <div className={`h-6 w-px ${theme === "dark" ? "bg-[#2C2C2C]" : "bg-gray-300"}`} />
            <input
                type="tel"
                name="phone"
                value={phoneNumber}
                onChange={onPhoneNumberChange}
                placeholder={placeholder}
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={15}
                className={`flex-1 bg-transparent px-3 py-2 outline-none text-sm ${theme === "dark" ? "text-gray-100 placeholder-gray-500" : "text-gray-900 placeholder-gray-400"
                    }`}
            />
        </div>
    );
};

export default CountryPhoneInput;
